const DATABASE_NAME = 'assessnow-response-outbox';
const DATABASE_VERSION = 1;
const ENTRY_STORE = 'entries';
const KEY_STORE = 'keys';
const KEY_ID = 'device-response-key';
const MAX_ENTRY_AGE_MS = 72 * 60 * 60 * 1000;
const STATUS_EVENT = 'assessnow:response-sync';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const supportsProtectedOutbox = () => Boolean(
  globalThis.indexedDB && globalThis.crypto?.subtle
);

const openDatabase = () => new Promise((resolve, reject) => {
  if (!supportsProtectedOutbox()) {
    reject(new Error('Protected browser storage is unavailable'));
    return;
  }
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(ENTRY_STORE)) {
      database.createObjectStore(ENTRY_STORE, { keyPath: 'id' });
    }
    if (!database.objectStoreNames.contains(KEY_STORE)) {
      database.createObjectStore(KEY_STORE, { keyPath: 'id' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Unable to open protected response storage'));
});

const requestResult = request => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Protected response storage failed'));
});

const withStore = async (storeName, mode, operation) => {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(storeName, mode);
    const completed = new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error || new Error('Protected response transaction failed'));
      transaction.onabort = () => reject(transaction.error || new Error('Protected response transaction was aborted'));
    });
    const result = await operation(transaction.objectStore(storeName));
    await completed;
    return result;
  } finally {
    database.close();
  }
};

const getEncryptionKey = async () => {
  const existing = await withStore(KEY_STORE, 'readonly', store => requestResult(store.get(KEY_ID)));
  if (existing?.key) return existing.key;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  await withStore(KEY_STORE, 'readwrite', store => requestResult(store.put({ id: KEY_ID, key })));
  return key;
};

const encryptPayload = async payload => {
  const key = await getEncryptionKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(JSON.stringify(payload))
  );
  return { iv: Array.from(iv), ciphertext };
};

const decryptPayload = async entry => {
  const key = await getEncryptionKey();
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(entry.iv) },
    key,
    entry.ciphertext
  );
  return JSON.parse(decoder.decode(plaintext));
};

const emitStatus = detail => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail }));
  }
};

export const responseOutboxId = (performanceId, questionNumber) => (
  `${Number(performanceId)}:${String(questionNumber)}`
);

export async function queueProtectedResponses(performanceId, responses) {
  const questionNumbers = Object.keys(responses || {});
  if (!Number.isInteger(Number(performanceId)) || !questionNumbers.length) {
    throw new Error('A performance and at least one response are required');
  }
  const now = Date.now();
  const entries = await Promise.all(questionNumbers.map(async questionNumber => {
    const encrypted = await encryptPayload({ response: responses[questionNumber] });
    return {
      id: responseOutboxId(performanceId, questionNumber),
      performanceId: Number(performanceId),
      questionNumber: String(questionNumber),
      createdAt: now,
      updatedAt: now,
      attempts: 0,
      ...encrypted
    };
  }));
  await withStore(ENTRY_STORE, 'readwrite', store => Promise.all(
    entries.map(entry => requestResult(store.put(entry)))
  ));
  emitStatus({ state: 'protected', performanceId: Number(performanceId), pending: entries.length });
  return entries.map(entry => entry.id);
}

export async function listProtectedResponses(performanceId = null) {
  if (!supportsProtectedOutbox()) return [];
  const entries = await withStore(ENTRY_STORE, 'readonly', store => requestResult(store.getAll()));
  const cutoff = Date.now() - MAX_ENTRY_AGE_MS;
  const current = entries.filter(entry => entry.updatedAt >= cutoff);
  const expired = entries.filter(entry => entry.updatedAt < cutoff).map(entry => entry.id);
  if (expired.length) {
    await withStore(ENTRY_STORE, 'readwrite', store => Promise.all(
      expired.map(id => requestResult(store.delete(id)))
    ));
  }
  return current
    .filter(entry => performanceId == null || entry.performanceId === Number(performanceId))
    .sort((left, right) => left.updatedAt - right.updatedAt);
}

export async function readProtectedResponse(entry) {
  return decryptPayload(entry);
}

export async function acknowledgeProtectedResponses(ids) {
  if (!ids?.length || !supportsProtectedOutbox()) return;
  await withStore(ENTRY_STORE, 'readwrite', store => Promise.all(
    ids.map(id => requestResult(store.delete(id)))
  ));
}

export async function clearProtectedResponses(performanceId) {
  const entries = await listProtectedResponses(performanceId);
  await acknowledgeProtectedResponses(entries.map(entry => entry.id));
  emitStatus({ state: 'saved', performanceId: Number(performanceId), pending: 0 });
}

export async function markProtectedResponseAttempt(entry) {
  await withStore(ENTRY_STORE, 'readwrite', async store => {
    const current = await requestResult(store.get(entry.id));
    if (!current) return;
    await requestResult(store.put({
      ...current,
      attempts: Number(current.attempts || 0) + 1,
      lastAttemptAt: Date.now()
    }));
  });
}

export function publishResponseSyncStatus(detail) {
  emitStatus(detail);
}

export function subscribeToResponseSync(listener) {
  if (typeof window === 'undefined') return () => {};
  const handler = event => listener(event.detail || {});
  window.addEventListener(STATUS_EVENT, handler);
  return () => window.removeEventListener(STATUS_EVENT, handler);
}

export const RESPONSE_OUTBOX_MAX_AGE_MS = MAX_ENTRY_AGE_MS;
export const RESPONSE_OUTBOX_VERSION = 'protected-response-outbox-1.0.0';
